import { Link } from "react-router-dom";
import Grain from "@/components/Grain";
import { ArrowLeft } from "lucide-react";

const CONTACT_EMAIL = "contact@gotoasktasha.com";
const SITE = "gotoasktasha.com";

const H = ({ children }) => (
  <h2 className="mt-12 mb-4 font-serif text-2xl font-semibold text-cream md:text-3xl">{children}</h2>
);
const P = ({ children }) => (
  <p className="mb-4 font-sans text-base font-light leading-relaxed text-cream/75">{children}</p>
);

export default function Privacy() {
  return (
    <div className="dark relative min-h-screen bg-ink text-cream" data-testid="privacy-page">
      <Grain />
      <div className="container-tasha py-24">
        <Link
          to="/"
          className="mb-10 inline-flex items-center gap-2 font-sans text-sm uppercase tracking-widest text-gold hover:text-gold-light"
        >
          <ArrowLeft size={16} /> Back
        </Link>
        <span className="overline">Privacy</span>
        <h1 className="mt-4 font-serif text-4xl font-semibold leading-tight text-cream md:text-6xl">
          Privacy <span className="font-script text-gold">Policy</span>
        </h1>
        <p className="mt-5 font-sans text-sm text-cream/60" data-testid="privacy-operator-note">
          #AskTasha is operated by <span className="text-cream/85">Sister to Sister Staffing, LLC</span>.
        </p>
        <p className="mt-2 font-sans text-sm text-cream/50">
          Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </p>

        <div className="mt-10 max-w-3xl">
          <P>
            Sister to Sister Staffing, LLC ("Company", "we", "our"), doing business as
            #AskTasha, operates {SITE} and the Home Health Care Agency Class Series.
            This Privacy Policy explains what personal information we collect, how we use it,
            and the choices you have. By using our site or submitting information, you agree
            to the practices described here.
          </P>

          <H>Information We Collect</H>
          <P>
            We collect the information you voluntarily submit through our forms — your name, email
            address, and (optionally) your mobile phone number when registering for the free class,
            and your email when requesting the Launch Blueprint PDF.
          </P>

          <H>How We Use Your Information</H>
          <P>
            We use your information to (i) confirm your registration and deliver the resource you
            requested, (ii) send you class reminders and related updates about the #AskTasha class
            series, and (iii) respond to your inquiries. We do not sell your data.
          </P>

          <H>Mobile Information &amp; SMS — Consent, Frequency, Opt-Out</H>
          <div className="mb-6 rounded-xl border border-gold/30 bg-gold/[0.06] p-5">
            <p className="font-sans text-base font-semibold leading-relaxed text-cream">
              No mobile information will be shared with third parties or affiliates for
              marketing or promotional purposes. All the above categories exclude text
              messaging originator opt-in data and consent; this information will not be
              shared with any third parties.
            </p>
          </div>
          <P>
            <span className="text-cream/90">Program:</span> The #AskTasha class-reminder SMS
            program, operated by Sister to Sister Staffing, LLC, sends class reminders (about
            1–2 messages per class week) to individuals who register for the free Home Health
            Care Agency Class Series and check the optional SMS consent box.
          </P>
          <P>
            <span className="text-cream/90">Opt-in:</span> Users opt in to SMS only by
            checking an optional, unchecked consent box on the registration form at
            gotoasktasha.com. The checkbox reads: "Yes, send me class reminder texts from
            #AskTasha. About 1–2 msgs per class week. Msg &amp; data rates may apply. Reply
            HELP for help, STOP to opt out." Consent is not a condition of registration.
          </P>
          <P>
            <span className="text-cream/90">Message Frequency:</span> About 1–2 messages per
            class week.
          </P>
          <P>
            <span className="text-cream/90">Message and data rates may apply.</span> Standard
            carrier charges from your mobile provider may apply to messages you receive.
          </P>
          <P>
            <span className="text-cream/90">Opt-out:</span> Reply <span className="text-cream">STOP</span> to any message
            to unsubscribe immediately. You will receive a final confirmation message and no
            further messages will be sent to your number.
          </P>
          <P>
            <span className="text-cream/90">Help:</span> Reply <span className="text-cream">HELP</span> to any message
            for support, or contact us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-gold">{CONTACT_EMAIL}</a>.
          </P>
          <P>
            <span className="text-cream/90">Carrier disclaimer:</span> Carriers are not liable
            for delayed or undelivered messages.
          </P>
          <P>
            <span className="text-cream/90">Data handling for SMS:</span> Mobile numbers and
            SMS opt-in consent records are stored only for the purpose of operating this
            reminder program. We use Twilio as our SMS provider strictly to deliver the
            messages you have opted in to receive. <span className="text-cream/90">
            We do not sell, rent, share, or otherwise transfer mobile phone numbers or SMS
            opt-in/consent data to any third party or affiliate for their own marketing or
            promotional purposes.</span>
          </P>

          <H>Cookies & Analytics</H>
          <P>
            Our site uses standard browser storage to remember non-personal preferences (for
            example, your admin login state). We do not use third-party advertising trackers.
          </P>

          <H>Data Retention</H>
          <P>
            We retain registration information for as long as reasonably necessary to provide the
            class series and comply with legal obligations. You may request deletion of your
            information at any time by contacting us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-gold">{CONTACT_EMAIL}</a>.
          </P>

          <H>Your Rights</H>
          <P>
            You may request access to, correction of, or deletion of your personal information at
            any time. Contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-gold">{CONTACT_EMAIL}</a>.
          </P>

          <H>Children's Privacy</H>
          <P>
            Our services are intended for adults. We do not knowingly collect personal information
            from children under 13.
          </P>

          <H>Changes to This Policy</H>
          <P>
            We may update this Privacy Policy from time to time. Updates will be posted on this
            page with a revised "Last updated" date.
          </P>

          <H>Contact</H>
          <P>
            Questions? Email{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-gold">{CONTACT_EMAIL}</a>.
          </P>
        </div>
      </div>
    </div>
  );
}
